/**
 * Quick Quote modal.
 *
 * The stepping itself belongs to Multi Step for Contact Form 7 — this only
 * opens and closes the dialog, keeps the step rail and the counter in step with
 * whichever fieldset the plugin has made current, and gives the choice tiles
 * their selected state.
 *
 * The plugin fires no event of its own, so the current fieldset is watched with
 * a MutationObserver rather than by hooking its internals.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function QuickQuote() {
	const modal = document.querySelector( '[data-quick-quote]' );

	if ( ! modal ) {
		return;
	}

	const dialog = modal.querySelector( '.bf-qq__dialog' );
	const panel = modal.querySelector( '.bf-qq__panel' );
	const rail = modal.querySelector( '[data-quick-quote-rail]' );
	const steps = Array.from( modal.querySelectorAll( '[data-quick-quote-step]' ) );
	const counter = modal.querySelector( '[data-quick-quote-current]' );
	const prompt = modal.querySelector( '[data-quick-quote-prompt]' );
	const form = modal.querySelector( 'form.wpcf7-form' );
	const fieldsets = form ? Array.from( form.querySelectorAll( '.fieldset-cf7mls' ) ) : [];

	let lastTrigger = null;

	/* ----------------------------------------------------------- step rail */

	const sync = () => {
		if ( ! fieldsets.length ) {
			return;
		}

		const found = fieldsets.findIndex( ( set ) => set.classList.contains( 'cf7mls_current_fs' ) );
		const step = found < 0 ? 0 : found;

		steps.forEach( ( item, i ) => {
			item.classList.toggle( 'is-current', i === step );
			item.classList.toggle( 'is-done', i < step );
		} );

		if ( counter ) {
			counter.textContent = String( step + 1 );
		}

		if ( prompt ) {
			prompt.textContent = steps[ step ]?.querySelector( '.bf-qq__step-text' )?.textContent || '';
		}

		// The panel scrolls on its own, so a new step starts at the top.
		panel?.scrollTo( { top: 0 } );

		// On mobile the rail is a horizontal strip, so bring the step the
		// visitor is on into view. The target comes from the item's layout
		// offset rather than the live scrollLeft, which would still be moving
		// if a previous scroll had not finished.
		const item = steps[ step ];

		if ( rail && item && rail.scrollWidth > rail.clientWidth ) {
			// Instant: the step change is already an animation, and a second
			// one running underneath it reads as lag.
			rail.scrollLeft = item.offsetLeft;
		}
	};

	// Reopening always starts at the beginning; what the visitor typed is left
	// in place so nothing is lost if they closed the dialog by accident.
	const resetToFirstStep = () => {
		if ( ! fieldsets.length ) {
			return;
		}

		fieldsets.forEach( ( set, i ) => {
			set.classList.toggle( 'cf7mls_current_fs', i === 0 );
			set.classList.remove( 'cf7mls_back_fs' );
		} );

		// Drop any error state left over from the previous visit.
		form?.querySelectorAll( '.wpcf7-not-valid' ).forEach( ( el ) => el.classList.remove( 'wpcf7-not-valid' ) );
		form?.querySelectorAll( '.wpcf7-not-valid-tip' ).forEach( ( el ) => el.remove() );
		form?.querySelectorAll( '.cf7mls-invalid' ).forEach( ( el ) => el.classList.remove( 'cf7mls-invalid' ) );

		sync();
	};

	/* ---------------------------------------------------------- open/close */

	const open = ( trigger ) => {
		lastTrigger = trigger || null;

		resetToFirstStep();

		modal.hidden = false;
		document.documentElement.classList.add( 'bf-qq-open' );

		// Let the browser paint the hidden -> shown change before the
		// transition class lands, so the modal animates in.
		requestAnimationFrame( () => modal.classList.add( 'is-open' ) );

		dialog?.focus();
	};

	const close = () => {
		modal.classList.remove( 'is-open' );
		document.documentElement.classList.remove( 'bf-qq-open' );

		const finish = () => {
			modal.hidden = true;
			lastTrigger?.focus();
			lastTrigger = null;
		};

		if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
			finish();

			return;
		}

		window.setTimeout( finish, 200 );
	};

	// Any link that already points at the quick quote page is a trigger, so
	// nothing else in the theme had to gain a data attribute.
	document.addEventListener( 'click', ( event ) => {
		const trigger = event.target.closest( 'a[href*="/quick-quote"], [data-quick-quote-open]' );

		if ( ! trigger || trigger.closest( '[data-quick-quote]' ) ) {
			return;
		}

		event.preventDefault();
		open( trigger );
	} );

	modal.querySelectorAll( '[data-quick-quote-close]' ).forEach( ( button ) => {
		button.addEventListener( 'click', close );
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( modal.hidden ) {
			return;
		}

		if ( event.key === 'Escape' ) {
			close();

			return;
		}

		// Keep tabbing inside the dialog while it is open.
		if ( event.key !== 'Tab' ) {
			return;
		}

		const items = Array.from( dialog.querySelectorAll( FOCUSABLE ) ).filter(
			( el ) => el.offsetParent !== null
		);

		if ( ! items.length ) {
			return;
		}

		const first = items[ 0 ];
		const last = items[ items.length - 1 ];

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	} );

	if ( ! form ) {
		return;
	}

	/* ------------------------------------------------------------ observers */

	if ( fieldsets.length ) {
		const observer = new MutationObserver( sync );

		fieldsets.forEach( ( set ) =>
			observer.observe( set, { attributes: true, attributeFilter: [ 'class' ] } )
		);

		sync();
	}

	/* ---------------------------------------------------------------- tiles */

	// CF7 gives the radio's wrapper no state of its own, so the tile carries it.
	const markTiles = () => {
		form.querySelectorAll( '.bf-qq-tiles .wpcf7-list-item' ).forEach( ( item ) => {
			const input = item.querySelector( 'input[type="radio"]' );

			item.classList.toggle( 'is-selected', !! input?.checked );
		} );
	};

	form.addEventListener( 'change', ( event ) => {
		if ( event.target.matches( '.bf-qq-tiles input[type="radio"]' ) ) {
			markTiles();
		}
	} );

	markTiles();

	// A completed send clears the fields, so clear the tiles with them.
	document.addEventListener( 'wpcf7mailsent', ( event ) => {
		if ( ! form.contains( event.target ) && event.target !== form ) {
			return;
		}

		window.setTimeout( () => {
			markTiles();
			sync();
		}, 100 );
	} );
}
